import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../features/userSlice";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { createCabinetQuote, getCabinetFormTemplateConfig } from "./api";
import { CABINET_QUOTE_STATUSES, normalizeCabinetQuoteStatuses } from "./constants";
import { CQ_FIELD, CQ_LABEL, Notice } from "./ui";
import { PageHeader, PageShell } from "../../components/UI/Page";

const LABEL_CLASS = CQ_LABEL;
const FIELD_CLASS = CQ_FIELD;
// Sections with many columns get smushed on one row; auto-fit keeps each field at a
// usable minimum width and wraps to the next line instead of smashing them together.
const WRAP_GRID_CLASS = "grid gap-4 grid-cols-1 sm:grid-cols-[repeat(auto-fit,minmax(170px,1fr))]";

const getGridClass = (columns) => {
  if (columns >= 5) return WRAP_GRID_CLASS;
  if (columns === 4) return "grid grid-cols-1 md:grid-cols-4 gap-4";
  if (columns === 3) return "grid grid-cols-1 md:grid-cols-3 gap-4";
  if (columns === 2) return "grid grid-cols-1 md:grid-cols-2 gap-4";
  return "grid grid-cols-1 gap-4";
};

const buildInitialRepeatableRows = (template) =>
  Object.fromEntries(
    (template?.sections || [])
      .filter((section) => section?.repeatable)
      .map((section) => [section.id, [{}]])
  );

function NewCabinetQuote() {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [jobAddress, setJobAddress] = useState("");
  const [quoteStatuses, setQuoteStatuses] = useState([...CABINET_QUOTE_STATUSES]);
  const [status, setStatus] = useState("draft");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [formConfig, setFormConfig] = useState({ templates: [] });
  const [selectedTemplateId, setSelectedTemplateId] = useState("default");
  const [singleValues, setSingleValues] = useState({});
  const [repeatableValues, setRepeatableValues] = useState({});
  const [stepIndex, setStepIndex] = useState(0);
  const [sectionNotes, setSectionNotes] = useState({});

  useEffect(() => {
    if (!user?.userID) return;

    getCabinetFormTemplateConfig(user.userID)
      .then((config) => {
        setFormConfig(config || { templates: [] });
        const statuses = normalizeCabinetQuoteStatuses(config?.quote_statuses);
        setQuoteStatuses(statuses);
        setStatus((prev) => (statuses.includes(prev) ? prev : statuses[0] || "draft"));
        const firstTemplate = config?.templates?.[0] || null;
        const firstTemplateId = firstTemplate?.id || "default";
        setSelectedTemplateId(firstTemplateId);
        setSingleValues({});
        setRepeatableValues(buildInitialRepeatableRows(firstTemplate));
      })
      .catch((loadError) => setError(loadError?.message || "Unable to load form templates"));
  }, [user]);

  const selectedTemplate = useMemo(
    () => (formConfig?.templates || []).find((template) => template.id === selectedTemplateId),
    [formConfig, selectedTemplateId]
  );

  const steps = useMemo(() => {
    const base = [{ id: "details", title: "Quote Details", hint: "Customer info" }];
    const sectionSteps = (selectedTemplate?.sections || []).map((section) => ({
      id: `section:${section.id}`,
      title: section.title || "Section",
      section,
    }));
    return [...base, ...sectionSteps];
  }, [selectedTemplate]);

  // Keep the active step valid if the template (and therefore step count) changes.
  useEffect(() => {
    setStepIndex((prev) => Math.min(prev, steps.length - 1));
  }, [steps.length]);

  useEffect(() => {
    setSingleValues({});
    setRepeatableValues(buildInitialRepeatableRows(selectedTemplate));
  }, [selectedTemplateId, selectedTemplate]);

  const onDynamicFieldChange = (fieldId, value) => {
    setSingleValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const onRepeatableFieldChange = (sectionId, rowIndex, fieldId, value) => {
    setRepeatableValues((prev) => {
      const rows = [...(prev[sectionId] || [{}])];
      rows[rowIndex] = { ...(rows[rowIndex] || {}), [fieldId]: value };
      return { ...prev, [sectionId]: rows };
    });
  };

  const addRepeatableRow = (sectionId) => {
    setRepeatableValues((prev) => ({ ...prev, [sectionId]: [...(prev[sectionId] || [{}]), {}] }));
  };

  const removeRepeatableRow = (sectionId, rowIndex) => {
    setRepeatableValues((prev) => {
      const next = (prev[sectionId] || []).filter((_, index) => index !== rowIndex);
      return { ...prev, [sectionId]: next.length > 0 ? next : [{}] };
    });
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!user?.userID) {
      setError("Unable to determine the logged-in user.");
      return;
    }

    const detailsStepIndex = steps.findIndex((step) => step.id === "details");
    const goToSectionStep = (sectionId) => {
      const target = steps.findIndex((step) => step.id === `section:${sectionId}`);
      if (target >= 0) {
        setStepIndex(target);
      }
    };

    if (!title.trim()) {
      if (detailsStepIndex >= 0) setStepIndex(detailsStepIndex);
      setError("Quote title is required.");
      return;
    }

    if (!customerName.trim() || !customerPhone.trim() || !customerEmail.trim() || !jobAddress.trim()) {
      if (detailsStepIndex >= 0) setStepIndex(detailsStepIndex);
      setError("Please complete all required quote fields.");
      return;
    }

    for (const section of selectedTemplate?.sections || []) {
      const fields = section?.fields || [];
      const required = fields.filter((field) => field?.required);

      if (section?.repeatable) {
        const rows = repeatableValues[section.id] || [{}];
        for (const row of rows) {
          const missing = required.find((field) => !String(row?.[field.id] || "").trim());
          if (missing) {
            goToSectionStep(section.id);
            setError(`Please complete required field: ${missing.label}`);
            return;
          }
        }
      } else {
        const missing = required.find((field) => !String(singleValues[field.id] || "").trim());
        if (missing) {
          goToSectionStep(section.id);
          setError(`Please complete required field: ${missing.label}`);
          return;
        }
      }
    }

    setSaving(true);
    try {
      const created = await createCabinetQuote({
        title: title.trim(),
        customer: {
          name: customerName.trim(),
          phone: customerPhone.trim(),
          email: customerEmail.trim(),
          job_address: jobAddress.trim(),
        },
        owner_name: user?.name,
        userId: user.userID,
        status,
        selected_form_template_id: selectedTemplateId,
        form_values: {
          single: singleValues,
          repeatable: repeatableValues,
          notes: sectionNotes,
        },
      });

      if (created?._id) {
        navigate(`/cabinet-quotes/${created._id}`);
        return;
      }

      setError("Quote was created, but no id was returned.");
    } catch (submitError) {
      setError(submitError?.message || "Unable to create quote right now.");
    } finally {
      setSaving(false);
    }
  };

  const safeIndex = Math.max(0, Math.min(stepIndex, steps.length - 1));
  const currentStep = steps[safeIndex] || steps[0];
  const isLastStep = safeIndex === steps.length - 1;
  const goPrev = () => setStepIndex((prev) => Math.max(0, prev - 1));
  const goNext = () => setStepIndex((prev) => Math.min(steps.length - 1, prev + 1));

  const renderSectionFields = (section) =>
    section.repeatable ? (
      <div className="space-y-3">
        {(repeatableValues[section.id] || [{}]).map((row, rowIndex) => (
          <div key={`${section.id}_${rowIndex}`} className="app-surface space-y-3 rounded-xl border p-4">
            <div className="flex items-center justify-between">
              <span className="app-eyebrow tabular-nums">Row {rowIndex + 1}</span>
              <button className="app-btn app-btn-ghost app-btn-sm !text-rose-700 dark:!text-rose-300" type="button" onClick={() => removeRepeatableRow(section.id, rowIndex)}>
                Remove Row
              </button>
            </div>
            <div className={WRAP_GRID_CLASS}>
              {(section.fields || []).map((field) => (
                <div key={`${field.id}_${rowIndex}`}>
                  <label className={LABEL_CLASS}>{field.label}{field.required ? " *" : ""}</label>
                  {field.type === "select" ? (
                    <select className={FIELD_CLASS} value={row[field.id] || ""} onChange={(event) => onRepeatableFieldChange(section.id, rowIndex, field.id, event.target.value)} required={field.required}>
                      <option value="">Select an option</option>
                      {(field.options || []).map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : (
                    <input className={FIELD_CLASS} value={row[field.id] || ""} onChange={(event) => onRepeatableFieldChange(section.id, rowIndex, field.id, event.target.value)} placeholder={field.label} required={field.required} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    ) : (
      <div className={getGridClass(section.columns)}>
        {(section.fields || []).map((field) => (
          <div key={field.id}>
            <label className={LABEL_CLASS}>{field.label}{field.required ? " *" : ""}</label>
            {field.type === "select" ? (
              <select className={FIELD_CLASS} value={singleValues[field.id] || ""} onChange={(event) => onDynamicFieldChange(field.id, event.target.value)} required={field.required}>
                <option value="">Select an option</option>
                {(field.options || []).map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            ) : (
              <input className={FIELD_CLASS} value={singleValues[field.id] || ""} onChange={(event) => onDynamicFieldChange(field.id, event.target.value)} placeholder={field.label} required={field.required} />
            )}
          </div>
        ))}
      </div>
    );

  const renderActiveStep = () => {
    if (currentStep?.id === "details") {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className={LABEL_CLASS}>Quote Title *</label>
            <input className={FIELD_CLASS} value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Kitchen Remodel - Smith" required />
          </div>
          <div>
            <label className={LABEL_CLASS}>Customer Name *</label>
            <input className={FIELD_CLASS} value={customerName} onChange={(event) => setCustomerName(event.target.value)} placeholder="Customer Name" required />
          </div>
          <div>
            <label className={LABEL_CLASS}>Customer Phone *</label>
            <input className={FIELD_CLASS} value={customerPhone} onChange={(event) => setCustomerPhone(event.target.value)} placeholder="(555) 555-5555" required />
          </div>
          <div>
            <label className={LABEL_CLASS}>Customer Email *</label>
            <input className={FIELD_CLASS} type="email" value={customerEmail} onChange={(event) => setCustomerEmail(event.target.value)} placeholder="customer@example.com" required />
          </div>
          <div>
            <label className={LABEL_CLASS}>Job Address *</label>
            <input className={FIELD_CLASS} value={jobAddress} onChange={(event) => setJobAddress(event.target.value)} placeholder="123 Main St, City, ST" required />
          </div>
        </div>
      );
    }

    if (currentStep?.section) {
      return renderSectionFields(currentStep.section);
    }

    return null;
  };

  const selectSmClass = "app-input rounded-xl border px-3 py-2 text-sm";

  return (
    <PageShell size="2xl">
      <style>{`@keyframes cqStepFade{from{opacity:0}to{opacity:1}}`}</style>

      <PageHeader
        icon={NoteAddIcon}
        tone="amber"
        eyebrow="Cabinets"
        title="Create Cabinet Quote"
        description="Work through each step — you'll continue straight into the workspace."
        actions={
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label htmlFor="cq-form-template" className="app-muted mb-1 block text-xs font-medium">Form Template</label>
              <select
                id="cq-form-template"
                className={selectSmClass}
                value={selectedTemplateId}
                onChange={(event) => setSelectedTemplateId(event.target.value)}
                title={selectedTemplate?.description || ""}
              >
                {(formConfig?.templates || []).map((template) => (
                  <option key={template.id} value={template.id}>{template.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="cq-initial-status" className="app-muted mb-1 block text-xs font-medium">Initial Status</label>
              <select
                id="cq-initial-status"
                className={selectSmClass}
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                {quoteStatuses.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
            </div>
          </div>
        }
      />

      <div className="flex flex-col gap-6 xl:flex-row xl:items-start">
        <div className="w-full min-w-0 xl:flex-1">
      <nav aria-label="Quote steps" className="-mx-1 mb-4 flex items-center gap-2 overflow-x-auto px-1 pb-2">
        {steps.map((step, i) => {
          const isCurrent = i === safeIndex;
          const isDone = i < safeIndex;
          return (
            <button
              key={step.id}
              type="button"
              onClick={() => setStepIndex(i)}
              aria-current={isCurrent ? "step" : undefined}
              className={`flex min-h-[2.5rem] shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                isCurrent
                  ? "border-transparent bg-[color:var(--app-accent-solid)] text-white shadow-sm"
                  : "app-card hover:bg-[color:var(--app-hover)]"
              }`}
            >
              <span
                className={`flex h-5 w-5 items-center justify-center rounded-full text-xs font-semibold tabular-nums ${
                  isCurrent
                    ? "bg-white/25 text-white"
                    : isDone
                    ? "bg-sky-500/15 text-sky-700 dark:text-sky-300"
                    : "bg-[color:var(--app-hover)]"
                }`}
              >
                {i + 1}
              </span>
              <span className="whitespace-nowrap">{step.title}</span>
            </button>
          );
        })}
      </nav>

      <form
        onSubmit={onSubmit}
        onKeyDown={(event) => {
          if (event.key === "Enter" && event.target.tagName !== "TEXTAREA" && !isLastStep) {
            event.preventDefault();
          }
        }}
      >
        <section className="app-card w-full rounded-2xl border p-5">
          <div className="app-divider mb-5 flex items-center justify-between gap-3 border-b pb-4">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-500/15 text-sm font-bold tabular-nums text-sky-700 dark:text-sky-300">{safeIndex + 1}</span>
              <div className="min-w-0">
                <h2 className="truncate text-lg font-semibold">{currentStep?.title}</h2>
                {currentStep?.hint && <p className="app-muted text-sm">{currentStep.hint}</p>}
              </div>
            </div>
            {currentStep?.section?.repeatable && (
              <button className="app-btn app-btn-secondary app-btn-sm shrink-0" type="button" onClick={() => addRepeatableRow(currentStep.section.id)}>
                + Add Row
              </button>
            )}
          </div>

          <div key={safeIndex} style={{ animation: "cqStepFade 220ms ease-out" }}>
            {renderActiveStep()}
          </div>
        </section>

        {error && (
          <Notice tone="rose" className="mt-4" role="alert">
            {error}
          </Notice>
        )}

        {/* Floating action bar. The right padding keeps the buttons clear of
            the feature-request bulb that floats in the bottom-right corner. */}
        <div data-bottom-bar="actions" className="app-glass app-glass-rim sticky bottom-4 z-20 mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-3 py-3 sm:px-4">
          <button
            type="button"
            className="app-btn app-btn-secondary"
            onClick={goPrev}
            disabled={safeIndex === 0 || saving}
          >
            ← Back
          </button>

          <span className="app-muted hidden whitespace-nowrap text-xs tabular-nums sm:inline">Step {safeIndex + 1} of {steps.length}</span>

          <div className="flex items-center gap-2">
            <button
              type="button"
              className="app-btn app-btn-ghost"
              onClick={() => navigate("/cabinet-quotes")}
              disabled={saving}
            >
              Cancel
            </button>
            {isLastStep ? (
              <button className="app-btn app-btn-primary" type="submit" disabled={saving}>
                {saving ? "Creating..." : "Create Quote"}
              </button>
            ) : (
              <button className="app-btn app-btn-primary" type="button" onClick={goNext}>
                Next →
              </button>
            )}
          </div>
        </div>
      </form>
        </div>

        <aside className="app-card w-full rounded-2xl border p-4 xl:sticky xl:top-24 xl:w-80 xl:shrink-0">
          <div className="mb-1 flex items-center gap-2">
            <EditNoteIcon fontSize="small" className="app-muted" />
            <h3 className="text-sm font-semibold">Notes — {currentStep?.title}</h3>
          </div>
          <p className="app-muted mb-3 text-xs">Jot down notes for this step. They're saved with the quote for your reference.</p>
          <textarea
            className={`${FIELD_CLASS} h-64 resize-y`}
            aria-label={`Notes for ${currentStep?.title || "this step"}`}
            value={sectionNotes[currentStep?.id] || ""}
            onChange={(event) => {
              const stepId = currentStep?.id;
              if (!stepId) return;
              setSectionNotes((prev) => ({ ...prev, [stepId]: event.target.value }));
            }}
            placeholder="Type here for notes…"
          />
        </aside>
      </div>
    </PageShell>
  );
}

export default NewCabinetQuote;
